import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSET_MAINTENANCE_TYPE, ASSET_METADATA, ASSET_TYPE, Asset, AssetMaintenance, AssetsUser, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { AssetMaintenanceService, AssetService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class AssetMaintenanceItemComponentBase extends CrudItemComponent<AssetMaintenance, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  assetSelectionList: Asset[];

  maintenanceTypeSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected assetMaintenanceService: AssetMaintenanceService,
    protected assetService: AssetService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(ASSET_MAINTENANCE_TYPE, fb, elementRef, router, route, userStorage, dialog, assetMaintenanceService, injector, snackbar);
  }

  createNewItem() {
    return new AssetMaintenance();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      assetMaintenanceId: [null, []],
      asset: [null, [requiredValidator,]],
      assetId: [null, [requiredValidator,]],
      comments: [null, [requiredValidator,]],
      createdBy: [null, []],
      createdDate: [null, []],
      lockVersion: [null, []],
      maintenanceBy: [null, [requiredValidator,]],
      maintenanceDate: [null, [requiredValidator,]],
      maintenanceType: [null, [requiredValidator,]]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.assetMaintenanceId?.disable();
    }
  }
  
  assetMaintenanceIdChanging(value: number, unsetDependents: boolean = true) {}
  
  assetMaintenanceIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetMaintenanceIdChangedByUser(value, unsetDependents);
    }
  }  
  
  assetMaintenanceIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  assetDisplay(item: Asset) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE.description(item);
  }
  
  assetLoadSelectionList() {
    this.assetService.findAll().subscribe(
      data => {
        this.assetSelectionList = ASSET_TYPE.entityDecorator.ensureContained(this.item && this.item.asset, data.items);
      }
    );
  }

  assetChanging(value: Asset, unsetDependents: boolean = true) {}
  
  assetChanged(value: Asset, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetChangedByUser(value, unsetDependents);
    }
  }  
  
  assetChangedByUser(value: Asset, unsetDependents: boolean = true) {
  }
  
  assetCompare(a: any, b: any) {
    return ASSET_METADATA.equals(a, b);
  }
  
  assetIdChanging(value: number, unsetDependents: boolean = true) {}
  
  assetIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetIdChangedByUser(value, unsetDependents);
    }
  }  
  
  assetIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  commentsChanging(value: string, unsetDependents: boolean = true) {}
  
  commentsChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsChangedByUser(value, unsetDependents);
    }
  }  
  
  commentsChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  maintenanceByChanging(value: string, unsetDependents: boolean = true) {}
  
  maintenanceByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceByChangedByUser(value, unsetDependents);
    }
  }  
  
  maintenanceByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  maintenanceDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  maintenanceDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceDateChangedByUser(value, unsetDependents);
    }
  }  
  
  maintenanceDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  maintenanceTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('MAINTENANCE_TYPE').subscribe(
      data => {
        this.maintenanceTypeSelectionList = data;
      }
    );
  }

  maintenanceTypeChanging(value: string, unsetDependents: boolean = true) {}
  
  maintenanceTypeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  maintenanceTypeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get assetMaintenanceId() {
    return this.control('assetMaintenanceId');
  }
  
  get asset() {
    return this.control('asset');
  }
  
  get assetId() {
    return this.control('assetId');
  }
  
  get comments() {
    return this.control('comments');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get maintenanceBy() {
    return this.control('maintenanceBy');
  }
  
  get maintenanceDate() {
    return this.control('maintenanceDate');
  }
  
  get maintenanceType() {
    return this.control('maintenanceType');
  }
  
}





